import { Row, Col, Button, Card, Image, Form } from "react-bootstrap"
import { useState } from "react"
import { useParams } from "react-router-dom"
import { useCallback, useEffect, useMemo } from "react"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import Select from 'components/general/ViewportSelect'
import { getSocket } from '../../../socket'
import { getInitialsFromName, resolveAvatarSrc } from 'utils/avatar'
import useUserAvatar from '../useUserAvatar'
import { getContestPluginConfig } from '../../../services/contestPlugin'
import { isMemberEquivalent } from 'utils/acl'
import { getTimerColors } from 'utils/timerColors'
import { getDebateTurns } from 'utils/debateTurns'
import { getActivityTimingLimits } from 'utils/activityTiming'
import DebateAgendaTimers from '../DebateAgendaTimers'

function TimerReportAvatar({ user, meeting, initials }) {
  const { avatarSrc, onAvatarError } = useUserAvatar(user, meeting);
  return avatarSrc
    ? <Image src={avatarSrc} alt='Timer presenter avatar' onError={onAvatarError} />
    : <span className='timer-report-avatar-fallback' aria-hidden='true'>{initials}</span>;
}

const timerStateKey = (agendaItemId, segmentId) => segmentId ? `${agendaItemId}:${segmentId}` : String(agendaItemId);

export default function TimerCard({ agenda, meeting, getMeeting, remoteDisplaysOnly = false }) {

  const url = process.env.REACT_APP_URL;
  const meetingId = useParams().meetingId;
  const { user: authUser } = useSelector((state) => state.auth);
  // const [isLoading, setIsLoading] = useState(false);
  const [timers, setTimers] = useState([]);
  const [resettingTimerId, setResettingTimerId] = useState('');
  const [devices, setDevices] = useState([]);
  const [registrationError, setRegistrationError] = useState('');
  const [displaySelections, setDisplaySelections] = useState({});
  const [selectedDeviceIds, setSelectedDeviceIds] = useState({});
  const [globalAgendaItemId, setGlobalAgendaItemId] = useState('');
  const [collapsedDeviceIds, setCollapsedDeviceIds] = useState({});
  const [timerStates, setTimerStates] = useState({});
  const [contestParticipantsByItem, setContestParticipantsByItem] = useState({});
  const [participantVisibilityByDevice, setParticipantVisibilityByDevice] = useState({});
  const [presenters, setPresenters] = useState({ officersAndMembers: [], guests: [] });
  const [presenterDropdownOpen, setPresenterDropdownOpen] = useState({});
  const [loadingPresenterChange, setLoadingPresenterChange] = useState({});
  const [polls, setPolls] = useState([]);
  const [pollFormOpenByItem, setPollFormOpenByItem] = useState({});
  const [selectedPollCandidatesByItem, setSelectedPollCandidatesByItem] = useState({});
  const [creatingPollByItem, setCreatingPollByItem] = useState({});
  const [updatingPollStatusByItem, setUpdatingPollStatusByItem] = useState({});

  const meetingSpecialAssignments = meeting?.meetingDetails?.specialAssignments || meeting?.specialAssignments || {};
  const authIdentityIds = [
    authUser?.user?.localUserId,
    authUser?.user?._id,
    authUser?.user?.id,
    authUser?.user?.tmAuthUserId,
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  const isTimerManager = Boolean(
    authIdentityIds.length
    && authIdentityIds.includes(String(meetingSpecialAssignments.timerManager || '').trim())
  );
  const isPollManager = Boolean(
    authIdentityIds.length
    && authIdentityIds.includes(String(meetingSpecialAssignments.pollManager || '').trim())
  );
  const isOfficerUser = String(authUser?.user?.acl || authUser?.acl || '').trim().toLowerCase() === 'officer';
  const canControlDisplays = isTimerManager;
  const canManagePolls = isTimerManager || isPollManager || isOfficerUser;


  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const fetchTimers = useCallback(async () => {
    // setIsLoading(true);
    try {
      const response = await fetch(`${url}/timers/${meetingId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      if (response.ok) {
        const data = await response.json();
        // console.log(data)
        setTimers(data);
      } else {
        console.error('Failed to fetch timers');
      }
    } catch (error) {
      console.error('Error fetching timers:', error);
    }
    // setIsLoading(false);
  }, [url, meetingId])

  const fetchPolls = useCallback(async () => {
    if (!meetingId) return;
    try {
      const response = await fetch(`${url}/polls/${meetingId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(authUser?.token ? { Authorization: `Bearer ${authUser.token}` } : {}),
        },
      });
      if (response.ok) {
        const data = await response.json();
        setPolls(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch polls:', error);
    }
  }, [url, meetingId, authUser?.token]);

  useEffect(() => {
    fetchTimers();
  }, [fetchTimers, agenda]);

  useEffect(() => {
    fetchPolls();
  }, [fetchPolls]);

  useEffect(() => {
    const socket = getSocket();
    const setRegisteredDevices = (data) => {
      const registeredDevices = (Array.isArray(data) ? data : [])
        .filter((device) => device.instanceDisplay === true)
        .map((device) => ({
        ...device,
        label: device.name,
        registeredDevice: true,
        }));
      setDevices(registeredDevices);
      setDisplaySelections((current) => {
        const next = { ...current };
        registeredDevices.forEach((device) => {
          next[device.deviceId] = {
            agendaItemId: device.currentAgendaItemId || '',
            showTimer: device.currentShowTimer !== false,
          };
        });
        return next;
      });
    };
    const loadRegisteredDevices = async () => {
      if (!authUser?.token || !meetingId || !isTimerManager) {
        setDevices([]);
        return;
      }
      try {
        const response = await fetch(`${url}/display-devices?meetingId=${encodeURIComponent(meetingId)}`, {
          headers: { Authorization: `Bearer ${authUser.token}` },
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.Error || 'Unable to load registered displays.');
        }
        setRegisteredDevices(data);
        setRegistrationError('');
      } catch (error) {
        setRegistrationError(error.message || 'Unable to load registered displays.');
      }
    };
    const updateTimerState = (payload) => {
      if (String(payload?.meetingId || '') !== String(meetingId) || !payload?.agendaItemId) return;
      const time = Number(payload.time);
      const key = timerStateKey(payload.agendaItemId, payload.segmentId);
      setTimerStates((current) => ({
        ...current,
        [key]: {
          time: Number.isFinite(time) ? Math.max(0, time) : current[key]?.time || 0,
          isPlaying: typeof payload.isPlaying === 'boolean'
            ? payload.isPlaying
            : current[key]?.isPlaying || false,
        },
      }));
    };
    const handleTimerReset = (payload) => {
      if (String(payload?.meetingId || '') !== String(meetingId) || !payload?.agendaItemId) return;
      setTimerStates((current) => ({
        ...current,
        [timerStateKey(payload.agendaItemId, payload.segmentId)]: { time: 0, isPlaying: false },
      }));
    };
    const handlePollStatusChanged = (payload) => {
      if (String(payload?.meetingId || '') !== String(meetingId)) return;
      setPolls((current) => {
        if (payload.status === 'deleted') {
          return current.filter((p) => String(p._id) !== String(payload.pollId));
        }
        const exists = current.some((p) => String(p._id) === String(payload.pollId));
        if (exists) {
          return current.map((p) =>
            String(p._id) === String(payload.pollId)
              ? { ...p, status: payload.status, title: payload.title || p.title }
              : p
          );
        }
        return current;
      });
      void fetchPolls();
    };
    const handleRemoteDisplayListChanged = (payload) => {
      if (String(payload?.meetingId || '') === String(meetingId)) {
        void loadRegisteredDevices();
      }
    };
    socket.on('meeting:timer-state', updateTimerState);
    socket.on('meeting:timer-tick', updateTimerState);
    socket.on('meeting:timer-reset', handleTimerReset);
    socket.on('poll:status-changed', handlePollStatusChanged);
    socket.on('meeting:remote-display-list-changed', handleRemoteDisplayListChanged);
    const handlePollCreatedOrDeleted = () => void fetchPolls();
    window.addEventListener('meeting:poll-created', handlePollCreatedOrDeleted);
    window.addEventListener('meeting:poll-deleted', handlePollCreatedOrDeleted);
    socket.emit('meeting:join', String(meetingId));
    void loadRegisteredDevices();
    const refreshInterval = window.setInterval(loadRegisteredDevices, 30000);
    return () => {
      window.clearInterval(refreshInterval);
      socket.off('meeting:timer-state', updateTimerState);
      socket.off('meeting:timer-tick', updateTimerState);
      socket.off('meeting:timer-reset', handleTimerReset);
      socket.off('poll:status-changed', handlePollStatusChanged);
      socket.off('meeting:remote-display-list-changed', handleRemoteDisplayListChanged);
      window.removeEventListener('meeting:poll-created', handlePollCreatedOrDeleted);
      window.removeEventListener('meeting:poll-deleted', handlePollCreatedOrDeleted);
    };
  }, [authUser?.token, fetchPolls, isTimerManager, meetingId, url]);

  const refreshDevices = () => {
    setRegistrationError('');
    fetch(`${url}/display-devices?meetingId=${encodeURIComponent(meetingId)}`, {
      headers: { Authorization: `Bearer ${authUser?.token}` },
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data?.Error || 'Unable to refresh displays.');
        const registeredDevices = (Array.isArray(data) ? data : [])
          .filter((device) => device.instanceDisplay === true)
          .map((device) => ({
          ...device,
          label: device.name,
          registeredDevice: true,
          }));
        setDevices(registeredDevices);
      })
      .catch((error) => setRegistrationError(error.message || 'Unable to refresh displays.'));
  };

  const toggleDeviceCollapsed = (deviceId) => {
    setCollapsedDeviceIds((current) => ({ ...current, [deviceId]: !current[deviceId] }));
  };

  const updateDisplaySelection = (deviceId, patch) => {
    setDisplaySelections((current) => ({
      ...current,
      [deviceId]: {
        agendaItemId: current[deviceId]?.agendaItemId || '',
        showTimer: current[deviceId]?.showTimer !== false,
        ...patch,
      }
    }));
  };

  const getDeviceCapability = (device, agendaItemId) => {
    const selectedAgendaItem = orderedAgenda.find((item) => String(item.agendaItemId) === String(agendaItemId));
    const supportedCapabilities = device.allowedCapabilities || device.capabilities || [];
    if (/poll/i.test(String(selectedAgendaItem?.agendaEvent?.eventType || ''))) {
      return device.capabilities?.includes('polls') && supportedCapabilities.includes('polls') ? 'polls' : '';
    }
    if (device.capabilities?.includes('timer') && supportedCapabilities.includes('timer')) return 'timer';
    if (device.capabilities?.includes('agenda') && supportedCapabilities.includes('agenda')) return 'agenda';
    if (!agendaItemId) return supportedCapabilities.find((capability) => device.capabilities?.includes(capability)) || '';
    return '';
  };

  const dispatchDisplayCommand = async (device, payload) => {
    if (device.registeredDevice) {
      try {
        const response = await fetch(`${url}/display-devices/${encodeURIComponent(device.deviceId)}/command`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser?.token}`,
          },
          body: JSON.stringify({
            meetingId,
            agendaItemId: payload.agendaItemId,
            showTimer: payload.showTimer,
            capability: getDeviceCapability(device, payload.agendaItemId),
          }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result?.Error || 'Unable to control the display.');
      } catch (error) {
        toast.error(error.message || 'Unable to control the display.', { position: 'bottom-left' });
      }
      return;
    }
    getSocket().emit('meeting:display-command', {
      meetingId,
      targetDeviceId: device.deviceId,
      ...payload,
    }, (result) => {
      if (!result?.ok) toast.error(result?.error || 'Unable to control the display.', { position: 'bottom-left' });
    });
  };

  const sendToSelectedDisplays = (close = false) => {
    const selectedDevices = devices.filter((device) => (
      device.registeredDevice && selectedDeviceIds[device.deviceId]
    ));
    if (!selectedDevices.length) {
      toast.error('Select at least one registered display.', { position: 'bottom-left' });
      return;
    }
    if (selectedDevices.some((device) => !device.connected)) {
      toast.error('A selected display is not connected. Reconnect it, or open its meeting tab and register it again.', { position: 'bottom-left' });
      return;
    }
    if (!close && !globalAgendaItemId) {
      toast.error('Select an agenda item first.', { position: 'bottom-left' });
      return;
    }
    const compatibleDevices = selectedDevices.filter((device) => (
      close || getDeviceCapability(device, globalAgendaItemId)
    ));
    if (compatibleDevices.length !== selectedDevices.length) {
      toast.error('The selected agenda item is not supported by every selected display.', { position: 'bottom-left' });
      return;
    }
    compatibleDevices.forEach((device) => {
      updateDisplaySelection(device.deviceId, { agendaItemId: close ? '' : globalAgendaItemId });
      void dispatchDisplayCommand(device, {
        agendaItemId: close ? null : globalAgendaItemId,
        showTimer: displaySelections[device.deviceId]?.showTimer !== false,
      });
    });
    if (close) setGlobalAgendaItemId('');
  };

  const toggleDisplayTimerVisibility = (deviceId) => {
    const selection = displaySelections[deviceId] || { agendaItemId: '', showTimer: true };
    const showTimer = selection.showTimer === false;
    updateDisplaySelection(deviceId, { showTimer });
    if (!selection.agendaItemId) return;

    const device = devices.find((entry) => entry.deviceId === deviceId);
    if (!device) return;
    dispatchDisplayCommand(device, {
      agendaItemId: selection.agendaItemId,
      showTimer,
    });
  };

  const sendDisplayCommand = (deviceId, close = false, agendaItemIdOverride = '') => {
    const selection = displaySelections[deviceId] || {};
    const agendaItemId = agendaItemIdOverride || selection.agendaItemId;
    if (!close && !agendaItemId) {
      toast.error('Select an agenda item first.', { position: 'bottom-left' });
      return;
    }
    const device = devices.find((entry) => entry.deviceId === deviceId);
    if (!device) return;
    dispatchDisplayCommand(device, {
      agendaItemId: close ? null : agendaItemId,
      showTimer: selection.showTimer !== false,
    });
  };

  const getPresenters = useCallback(async () => {
    try {
      const response = await fetch(`${url}/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser?.token}`
        }
      });
      if (response.status === 200) {
        const presentersData = await response.json();
        const officersAndMembers = presentersData.filter(p => isMemberEquivalent(p.acl)).sort((a, b) => a.firstName.localeCompare(b.firstName));
        const guests = presentersData.filter(p => p.acl === 'Guest').sort((a, b) => a.firstName.localeCompare(b.firstName));
        setPresenters({ officersAndMembers, guests });
      }
    } catch (error) {
      console.error('Failed to load presenters:', error);
    }
  }, [authUser?.token, url]);

  useEffect(() => {
    if (authUser?.token) {
      getPresenters();
    }
  }, [authUser?.token, getPresenters]);

  const presenterOptions = useMemo(() => [
    {
      label: 'Members',
      options: (presenters.officersAndMembers || []).map((presenter) => ({
        value: presenter._id,
        label: `${presenter.firstName} ${presenter.lastName}`.trim(),
      })),
    },
    {
      label: 'Guests',
      options: (presenters.guests || []).map((presenter) => ({
        value: presenter._id,
        label: `${presenter.firstName} ${presenter.lastName}`.trim(),
      })),
    },
  ], [presenters]);

  const selectStyles = useMemo(() => ({
    control: (provided, state) => ({
      ...provided,
      backgroundColor: '#23272b',
      color: '#fff',
      borderColor: state.isFocused ? '#FFD700' : '#444',
      boxShadow: state.isFocused ? '0 0 0 1px #FFD700' : 'none',
      minHeight: '38px',
      cursor: 'pointer',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 99999,
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#23272b',
      border: '1px solid #444',
      zIndex: 99999,
    }),
    groupHeading: (provided) => ({
      ...provided,
      color: '#FFD700',
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#FFD700'
        : state.isFocused
          ? 'rgba(255, 215, 0, 0.15)'
          : '#23272b',
      color: state.isSelected ? '#23272b' : '#fff',
      fontWeight: state.isSelected ? 600 : 400,
      cursor: 'pointer',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#fff',
    }),
    input: (provided) => ({
      ...provided,
      color: '#fff',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#888',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: '#FFD700',
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: '#aaa',
      ':hover': { color: '#ff6b6b' },
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: '#444',
    }),
  }), []);

  const handlePresenterChange = async (agendaItemId, selectedOption) => {
    if (!isTimerManager) return;

    const targetMeetingId = meeting?.meetingDetails?.id || meeting?._id || meetingId;
    const agendaItem = (Array.isArray(agenda) ? agenda : []).find((item) => String(item.agendaItemId) === String(agendaItemId));
    if (!agendaItem) return;

    setLoadingPresenterChange((current) => ({ ...current, [agendaItemId]: true }));
    try {
      if (!selectedOption || !selectedOption.value) {
        // Clearing / removing presenter
        if (agendaItem.roleAssignmentId) {
          const response = await fetch(`${url}/roleAssignment/${agendaItem.roleAssignmentId}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authUser?.token}`,
            },
          });
          if (!response.ok && response.status !== 404) throw new Error('Failed to remove presenter');
        }
        setPresenterDropdownOpen((current) => ({ ...current, [agendaItemId]: false }));
        await Promise.resolve(getMeeting?.());
        toast.success('Presenter removed', { position: 'bottom-left' });
        return;
      }

      if (agendaItem.roleAssignmentId) {
        const response = await fetch(`${url}/roleAssignment/${agendaItem.roleAssignmentId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authUser?.token}`,
          },
          body: JSON.stringify({ user: selectedOption.value }),
        });

        if (!response.ok) throw new Error('Failed to update presenter');
      } else {
        const roleId = agendaItem.meetingRole?._id || agendaItem.meetingRole?.id || agendaItem.meetingRole;
        if (!roleId) {
          throw new Error('This agenda item does not have an associated meeting role to assign.');
        }

        const response = await fetch(`${url}/roleAssignment/${targetMeetingId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authUser?.token}`,
          },
          body: JSON.stringify({
            role: roleId,
            user: selectedOption.value,
          }),
        });

        if (!response.ok) throw new Error('Failed to create role assignment');
      }

      setPresenterDropdownOpen((current) => ({ ...current, [agendaItemId]: false }));
      await Promise.resolve(getMeeting?.());
      toast.success('Presenter updated', { position: 'bottom-left' });
    } catch (error) {
      toast.error(error.message || 'Failed to update presenter', { position: 'bottom-left' });
    } finally {
      setLoadingPresenterChange((current) => ({ ...current, [agendaItemId]: false }));
    }
  };

  const getPollStatusColor = (status) => {
    switch (status) {
      case 'ready': return '#28a745';
      case 'active': return '#ffc107';
      case 'closed': return '#dc3545';
      case 'displayed': return '#0dcaf0';
      default: return '#fff';
    }
  };

  const handlePollStatusChange = async (pollId, nextStatus) => {
    if (!pollId || !nextStatus || !canManagePolls) return;
    setUpdatingPollStatusByItem((current) => ({ ...current, [pollId]: true }));
    try {
      const response = await fetch(`${url}/poll/${pollId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser?.token}`,
        },
        body: JSON.stringify({ status: nextStatus }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.Error || err?.error || response.statusText || 'Failed to update poll status');
      }

      setPolls((current) =>
        current.map((p) => (String(p._id) === String(pollId) ? { ...p, status: nextStatus } : p))
      );
      toast.success(`Poll status set to ${nextStatus}`, { position: 'bottom-left' });
    } catch (error) {
      toast.error(error.message || 'Failed to update poll status', { position: 'bottom-left' });
    } finally {
      setUpdatingPollStatusByItem((current) => ({ ...current, [pollId]: false }));
    }
  };

  const handleCreatePoll = async (agendaItemId, selectedAgendaItem) => {
    if (!canManagePolls) return;
    const selectedCandidateIds = selectedPollCandidatesByItem[agendaItemId] || [];
    if (selectedCandidateIds.length === 0) {
      toast.error('Please select at least one candidate agenda item.', { position: 'bottom-left' });
      return;
    }

    const selectedCandidateItems = orderedAgenda.filter((item) => selectedCandidateIds.includes(item.agendaItemId));
    const newPoll = {
      title: selectedAgendaItem.agendaEvent?.name,
      options: selectedCandidateItems.map((candidate) => ({
        firstName: candidate.assignedUser?.firstName || 'TBA',
        lastName: candidate.assignedUser?.lastName || '',
        userId: candidate.assignedUser?.id || candidate.assignedUser?._id || candidate.assignedUser?.tmAuthUserId || null,
        title: candidate.item_title || candidate.agendaEvent?.name || 'Candidate',
        votes: 0,
      })),
      meeting: meetingId,
      status: 'ready',
    };

    setCreatingPollByItem((current) => ({ ...current, [agendaItemId]: true }));
    try {
      const response = await fetch(`${url}/poll/${meetingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser?.token}`,
        },
        body: JSON.stringify(newPoll),
      });

      const raw = await response.text();
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(data?.Error || data?.error || response.statusText || 'Failed to create poll.');
      }

      const createdPoll = data || newPoll;
      window.dispatchEvent(new CustomEvent('meeting:poll-created', {
        detail: {
          meetingId,
          pollId: createdPoll?._id || createdPoll?.id || null,
          title: newPoll.title,
        },
      }));
      await fetchPolls();
      setPollFormOpenByItem((current) => ({ ...current, [agendaItemId]: false }));
      setSelectedPollCandidatesByItem((current) => ({ ...current, [agendaItemId]: [] }));
      toast.success('Poll created with status ready', { position: 'bottom-left' });
    } catch (error) {
      toast.error(error.message || 'Failed to create poll', { position: 'bottom-left' });
    } finally {
      setCreatingPollByItem((current) => ({ ...current, [agendaItemId]: false }));
    }
  };

  const handleDeletePoll = async (pollId) => {
    if (!canManagePolls || !pollId) return;
    try {
      const response = await fetch(`${url}/poll/${pollId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser?.token}`,
        },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.Error || err?.error || response.statusText || 'Failed to delete poll');
      }
      setPolls((current) => current.filter((p) => String(p._id) !== String(pollId)));
      window.dispatchEvent(new CustomEvent('meeting:poll-deleted', {
        detail: {
          meetingId,
          pollId,
        },
      }));
      toast.success('Poll deleted', { position: 'bottom-left' });
    } catch (error) {
      toast.error(error.message || 'Failed to delete poll', { position: 'bottom-left' });
    }
  };

  const togglePollForm = (agendaItemId) => {
    setPollFormOpenByItem((current) => ({ ...current, [agendaItemId]: !current[agendaItemId] }));
    if (!selectedPollCandidatesByItem[agendaItemId]) {
      setSelectedPollCandidatesByItem((current) => ({ ...current, [agendaItemId]: [] }));
    }
  };

  const togglePollCandidate = (agendaItemId, candidateItemId) => {
    setSelectedPollCandidatesByItem((current) => {
      const existing = current[agendaItemId] || [];
      const updated = existing.includes(candidateItemId)
        ? existing.filter((id) => id !== candidateItemId)
        : [...existing, candidateItemId];
      return { ...current, [agendaItemId]: updated };
    });
  };

  const getContestNameFromEvent = (eventName) => {
    const match = String(eventName || '').trim().match(/^Contest\s+(.+?)\s+Winners$/i);
    return match ? match[1].trim() : '';
  };

  const orderedAgenda = useMemo(() => [...(Array.isArray(agenda) ? agenda : [])]
    .sort((left, right) => Number(left?.position || 0) - Number(right?.position || 0)), [agenda]);

  useEffect(() => {
    const selectedIds = [...new Set(devices.map((device) => displaySelections[device.deviceId]?.agendaItemId).filter(Boolean))];
    selectedIds.forEach((agendaItemId) => {
      if (contestParticipantsByItem[agendaItemId]) return;
      const selectedItem = orderedAgenda.find((item) => String(item.agendaItemId) === String(agendaItemId));
      const contestName = getContestNameFromEvent(selectedItem?.agendaEvent?.name);
      if (!contestName || !authUser?.token) return;
      (async () => {
        try {
          const config = await getContestPluginConfig(authUser.token).catch(() => ({ contestTypes: [] }));
          const configured = (config.contestTypes || []).find((contest) => String(contest?.name || '').trim().toLowerCase().replace(/\s+contest$/, '').replace(/\bspeeches\b/g, 'speech') === contestName.toLowerCase().replace(/\bspeeches\b/g, 'speech'));
          const contestType = String(configured?._id || configured?.id || contestName).trim();
          const response = await fetch(`${url}/contesthub/meetings/${meetingId}/results?${new URLSearchParams({ contestType }).toString()}`, { headers: { Authorization: `Bearer ${authUser.token}` } });
          const payload = response.ok ? await response.json() : { results: [] };
          const result = Array.isArray(payload?.results) ? payload.results[0] : null;
          const placementByName = new Map((result?.placements || []).map((placement) => [String(placement?.name || '').trim().toLowerCase(), Number(placement?.place)]));
          const rankedNames = [...(result?.tiebreakerRanking || []), ...(result?.judgeRankings || []).flat()]
            .map((speaker) => String(speaker?.name || '').trim().toLowerCase()).filter(Boolean);
          const assignedIds = [...new Set((result?.agendaItemIds || []).map((id) => String(id || '').trim()).filter(Boolean))];
          const rankedAgendaItemIds = orderedAgenda
            .filter((agendaItem) => {
              const name = `${agendaItem?.assignedUser?.firstName || ''} ${agendaItem?.assignedUser?.lastName || ''}`.trim().toLowerCase();
              return name && rankedNames.includes(name);
            })
            .map((agendaItem) => String(agendaItem.agendaItemId || '').trim()).filter(Boolean);
          const participantItems = [];
          const participantKeys = new Set();
          [...assignedIds, ...rankedAgendaItemIds].forEach((id) => {
            const item = orderedAgenda.find((agendaItem) => String(agendaItem.agendaItemId) === id);
            const firstName = String(item?.assignedUser?.firstName || '').trim();
            const lastName = String(item?.assignedUser?.lastName || '').trim();
            const name = `${firstName} ${lastName}`.trim() || 'Participant';
            const userId = String(item?.assignedUser?.tmAuthUserId || item?.assignedUser?.id || item?.assignedUser?._id || '').trim();
            const participantKey = userId || name.toLowerCase() || id;
            if (participantKeys.has(participantKey)) return;
            participantKeys.add(participantKey);
            const normalizedName = name.toLowerCase();
            participantItems.push({ id, name, firstName, lastName, position: placementByName.get(normalizedName) || null });
          });
          // Match the displayed modal's order: placed winners first (by place), then
          // remaining participants alphabetically by first name, so remote toggle
          // numbers line up with the cards shown on the screen.
          const winners = participantItems
            .filter((participant) => participant.position && participant.position <= 3)
            .sort((left, right) => left.position - right.position);
          const others = participantItems
            .filter((participant) => !participant.position || participant.position > 3)
            .sort((left, right) => left.firstName.localeCompare(right.firstName) || left.lastName.localeCompare(right.lastName) || left.name.localeCompare(right.name));
          const participants = [...winners, ...others].map((participant, index) => ({
            ...participant,
            position: participant.position || index + 1,
          }));
          setContestParticipantsByItem((current) => ({ ...current, [agendaItemId]: participants }));
        } catch {
          setContestParticipantsByItem((current) => ({ ...current, [agendaItemId]: [] }));
        }
      })();
    });
  }, [authUser?.token, contestParticipantsByItem, devices, displaySelections, meetingId, orderedAgenda, url]);

  const toggleRemoteParticipant = (deviceId, agendaItemId, participantId) => {
    const current = participantVisibilityByDevice[deviceId]?.[participantId] === true;
    const visible = !current;
    setParticipantVisibilityByDevice((state) => ({
      ...state,
      [deviceId]: { ...(state[deviceId] || {}), [participantId]: visible },
    }));
    getSocket().emit('meeting:contest-participant-visibility', {
      meetingId,
      targetDeviceId: deviceId,
      agendaItemId,
      participantId,
      visible,
    });
  };

  const setAllRemoteParticipantsVisibility = (deviceId, agendaItemId, participants, visible) => {
    if (!participants.length) return;
    const currentVisibility = participantVisibilityByDevice[deviceId] || {};
    const nextVisibility = participants.reduce((state, participant) => ({
      ...state,
      [participant.id]: visible,
    }), { ...currentVisibility });
    setParticipantVisibilityByDevice((state) => ({ ...state, [deviceId]: nextVisibility }));
    const socket = getSocket();
    participants.forEach((participant) => socket.emit('meeting:contest-participant-visibility', {
      meetingId,
      targetDeviceId: deviceId,
      agendaItemId,
      participantId: participant.id,
      visible,
    }));
  };

  const toggleTitleVisibility = async (deviceId, agendaItem) => {
    const agendaItemId = String(agendaItem?.agendaItemId || '').trim();
    if (!agendaItemId || !authUser?.token) return;
    try {
      const response = await fetch(`${url}/agendaItem/${agendaItemId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authUser.token}`,
        },
        body: JSON.stringify({ hideTitleAndSubtitle: !agendaItem.hideTitleAndSubtitle }),
      });
      if (!response.ok) throw new Error('Unable to update agenda title visibility.');
      await Promise.resolve(getMeeting?.());
      sendDisplayCommand(deviceId, false, agendaItemId);
    } catch (error) {
      toast.error(error.message || 'Unable to update agenda title visibility.', { position: 'bottom-left' });
    }
  };

  const toggleSelectedTitleVisibility = async (agendaItem) => {
    const agendaItemId = String(agendaItem?.agendaItemId || '').trim();
    if (!agendaItemId || !authUser?.token || !selectedConnectedDevices.length) return;
    try {
      const response = await fetch(`${url}/agendaItem/${agendaItemId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authUser?.token || ''}`,
        },
        body: JSON.stringify({ hideTitleAndSubtitle: !agendaItem.hideTitleAndSubtitle }),
      });
      if (!response.ok) throw new Error('Unable to update agenda title visibility.');
      await Promise.resolve(getMeeting?.());
      selectedConnectedDevices.forEach((device) => {
        updateDisplaySelection(device.deviceId, { agendaItemId });
        void dispatchDisplayCommand(device, {
          agendaItemId,
          showTimer: displaySelections[device.deviceId]?.showTimer !== false,
        });
      });
    } catch (error) {
      toast.error(error.message || 'Unable to update agenda title visibility.', { position: 'bottom-left' });
    }
  };

  const setSelectedDisplaysTimerVisibility = (showTimer) => {
    selectedConnectedDevices.forEach((device) => {
      updateDisplaySelection(device.deviceId, { agendaItemId: globalAgendaItemId, showTimer });
      void dispatchDisplayCommand(device, { agendaItemId: globalAgendaItemId, showTimer });
    });
  };

  const orderedDevices = useMemo(() => [...devices].sort((left, right) => left.name.localeCompare(right.name)), [devices]);
  const selectedRegisteredDevices = devices.filter((device) => (
    device.registeredDevice && selectedDeviceIds[device.deviceId]
  ));
  const selectedConnectedDevices = selectedRegisteredDevices.filter((device) => device.connected);
  const selectedGroupAgenda = orderedAgenda.filter((item) => (
    selectedConnectedDevices.length > 0
    && selectedConnectedDevices.length === selectedRegisteredDevices.length
    && selectedConnectedDevices.every((device) => Boolean(getDeviceCapability(device, item.agendaItemId)))
  ));
  const selectedGlobalAgendaItem = orderedAgenda.find((item) => (
    String(item.agendaItemId) === String(globalAgendaItemId)
  ));
  const selectedGlobalTimerState = timerStates[String(globalAgendaItemId)] || {
    time: Number(selectedGlobalAgendaItem?.timer?.time || 0),
    isPlaying: false,
  };
  const selectedGlobalTimerColors = getTimerColors(
    selectedGlobalTimerState.time,
    selectedGlobalAgendaItem?.agendaEvent
  );
  const selectedGlobalPoll = selectedGlobalAgendaItem?.agendaEvent?.eventType === 'poll'
    ? polls.find((poll) => poll.title === selectedGlobalAgendaItem.agendaEvent?.name)
    : null;
  const selectedGlobalContestParticipants = contestParticipantsByItem[globalAgendaItemId] || [];

  const navigateDisplay = (deviceId, direction) => {
    const device = devices.find((entry) => entry.deviceId === deviceId);
    const visibleAgenda = orderedAgenda.filter((item) => Boolean(getDeviceCapability(device, item.agendaItemId)));
    const currentId = displaySelections[deviceId]?.agendaItemId || '';
    const currentIndex = visibleAgenda.findIndex((item) => String(item.agendaItemId) === String(currentId));
    const nextIndex = currentIndex < 0 && direction > 0 ? 0 : currentIndex + direction;
    const nextItem = visibleAgenda[nextIndex];
    if (nextItem) sendDisplayCommand(deviceId, false, nextItem.agendaItemId);
  };

  const navigateSelectedDisplays = (direction) => {
    if (!selectedGroupAgenda.length || selectedConnectedDevices.length !== selectedRegisteredDevices.length) return;
    const currentIndex = selectedGroupAgenda.findIndex((item) => (
      String(item.agendaItemId) === String(globalAgendaItemId)
    ));
    const nextIndex = currentIndex < 0 && direction > 0 ? 0 : currentIndex + direction;
    const nextItem = selectedGroupAgenda[nextIndex];
    if (!nextItem) return;
    setGlobalAgendaItemId(String(nextItem.agendaItemId));
    selectedConnectedDevices.forEach((device) => {
      updateDisplaySelection(device.deviceId, { agendaItemId: String(nextItem.agendaItemId) });
      void dispatchDisplayCommand(device, {
        agendaItemId: String(nextItem.agendaItemId),
        showTimer: displaySelections[device.deviceId]?.showTimer !== false,
      });
    });
  };

  const getSelectedTimerState = (agendaItemId) => {
    const item = orderedAgenda.find((agendaItem) => String(agendaItem.agendaItemId) === String(agendaItemId));
    return timerStates[String(agendaItemId)] || {
      time: Number(item?.timer?.time || 0),
      isPlaying: false,
    };
  };

  const saveTimerTime = async (agendaItemId, time) => {
    const item = orderedAgenda.find((agendaItem) => String(agendaItem.agendaItemId) === String(agendaItemId));
    const body = JSON.stringify({
      event: item?.agendaEvent?.name || 'Agenda item',
      name: `${item?.assignedUser?.firstName || 'TBA'} ${item?.assignedUser?.lastName || ''}`.trim(),
      time,
    });
    const request = (method) => fetch(`${url}/timer/${agendaItemId}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authUser.token}`,
      },
      body,
    });
    let response = await request('PATCH');
    if (response.status === 404) response = await request('PUT');
    if (!response.ok) throw new Error('Failed to save timer');
    await fetchTimers();
  };

  const toggleRowTimer = async (agendaItemId) => {
    if (!agendaItemId) return;
    const timerState = getSelectedTimerState(agendaItemId);
    const nextIsPlaying = !timerState.isPlaying;
    getSocket().emit('meeting:timer-state', {
      meetingId,
      agendaItemId,
      isPlaying: nextIsPlaying,
      time: timerState.time,
    });
    if (!nextIsPlaying) {
      try {
        await saveTimerTime(agendaItemId, timerState.time);
      } catch (error) {
        toast.error(error.message || 'Failed to save timer', { position: 'bottom-left' });
      }
    }
  };

  const agendaByItemId = useMemo(() => {
    const map = new Map();
    (Array.isArray(agenda) ? agenda : []).forEach((item) => {
      const itemId = String(item?.agendaItemId || '');
      if (!itemId) return;
      map.set(itemId, item);
    });
    return map;
  }, [agenda]);

  const handleResetTimer = useCallback(async (timer) => {
    const agendaItemId = String(timer?.agendaItem?.id || timer?.agendaItem?._id || '').trim();
    if (!agendaItemId || !authUser?.token) return;

    setResettingTimerId(String(timer?._id || agendaItemId));
    try {
      const segmentId = String(timer?.segmentId || '').trim();
      let response = await fetch(`${url}/timer-record/${encodeURIComponent(timer._id)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser.token}`,
        },
      });
      if (response.status === 404) {
        response = await fetch(
          segmentId
            ? `${url}/timer/${agendaItemId}/segment/${encodeURIComponent(segmentId)}`
            : `${url}/timer/${agendaItemId}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authUser.token}`,
            },
          }
        );
      }

      if (response.status !== 200) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.Error || payload?.error || payload?.message || `Failed to reset timer (${response.status})`);
      }

      const socket = getSocket();
      socket.emit('meeting:timer-reset', {
        meetingId,
        agendaItemId,
        ...(segmentId ? { segmentId } : {}),
      });
      if (!segmentId) {
        socket.emit('meeting:feedback-reset', {
          meetingId,
          agendaItemId,
        });
        window.dispatchEvent(new CustomEvent('pathway-feedback-reset', {
          detail: {
            meetingId,
            agendaItemId,
          },
        }));
      }

      window.dispatchEvent(new CustomEvent('meeting:timer-reset', {
        detail: {
          meetingId,
          agendaItemId,
          ...(segmentId ? { segmentId } : {}),
        },
      }));

      await Promise.resolve(getMeeting?.());
      await fetchTimers();
    } catch (error) {
      toast.error(error?.message || 'Failed to reset timer', { position: 'bottom-left' });
    } finally {
      setResettingTimerId('');
    }
  }, [authUser?.token, fetchTimers, getMeeting, meetingId, url]);

  return (
    <div className="timer-report-panel">
      {authUser?.token && canControlDisplays && remoteDisplaysOnly && (
        <>
          <Card className="timer-report-card remote-display-card mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Remote displays</h5>
                <Button className="remote-display-secondary-button" variant="outline-light" size="sm" onClick={refreshDevices} title="Refresh displays" aria-label="Refresh displays">
                  <i className="bi bi-arrow-clockwise" aria-hidden="true" />
                </Button>
              </div>
              {registrationError ? <p className="text-danger mb-0">{registrationError}</p> : devices.length === 0 ? <p className="mb-0">No screens have registered through Meeting Tools for this meeting.</p> : null}
              {devices.length > 0 && <p className="remote-display-muted small mb-0">“Not connected” means a registered meeting tab is no longer sharing.</p>}
              {devices.some((device) => device.registeredDevice) && (
                <div className="d-flex flex-wrap gap-2 mt-3">
                  <Form.Select
                    className="flex-grow-1"
                    style={{ minWidth: '220px' }}
                    value={globalAgendaItemId}
                    onChange={(event) => setGlobalAgendaItemId(event.target.value)}
                    aria-label="Agenda item for selected displays"
                  >
                    <option value="">Select agenda item for selected displays</option>
                    {(selectedRegisteredDevices.length ? orderedAgenda.filter((item) => (
                      selectedRegisteredDevices.every((device) => Boolean(getDeviceCapability(device, item.agendaItemId)))
                    )) : orderedAgenda).map((item) => (
                      <option key={item.agendaItemId} value={item.agendaItemId}>
                        {item.position}. {item.agendaEvent?.name || item.item_title || 'Agenda item'}
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    size="sm"
                    className="remote-display-secondary-button"
                    variant="outline-light"
                    disabled={!selectedGroupAgenda.length
                      || selectedConnectedDevices.length !== selectedRegisteredDevices.length
                      || selectedGroupAgenda.findIndex((item) => String(item.agendaItemId) === String(globalAgendaItemId)) <= 0}
                    onClick={() => navigateSelectedDisplays(-1)}
                    title="Previous agenda item on selected displays"
                    aria-label="Previous agenda item on selected displays"
                  >
                    <i className="bi bi-chevron-left" aria-hidden="true" />
                  </Button>
                  <Button
                    size="sm"
                    disabled={!globalAgendaItemId
                      || !selectedConnectedDevices.length
                      || selectedRegisteredDevices.some((device) => !getDeviceCapability(device, globalAgendaItemId))}
                    onClick={() => sendToSelectedDisplays(false)}
                  >
                    Display
                  </Button>
                  <Button size="sm" variant="outline-secondary" disabled={!selectedConnectedDevices.length} onClick={() => sendToSelectedDisplays(true)}>
                    Close
                  </Button>
                  <Button
                    size="sm"
                    className="remote-display-secondary-button"
                    variant="outline-light"
                    disabled={!selectedGroupAgenda.length
                      || selectedConnectedDevices.length !== selectedRegisteredDevices.length
                      || selectedGroupAgenda.findIndex((item) => String(item.agendaItemId) === String(globalAgendaItemId)) >= selectedGroupAgenda.length - 1}
                    onClick={() => navigateSelectedDisplays(1)}
                    title="Next agenda item on selected displays"
                    aria-label="Next agenda item on selected displays"
                  >
                    <i className="bi bi-chevron-right" aria-hidden="true" />
                  </Button>
                </div>
              )}
              {selectedGlobalAgendaItem && selectedConnectedDevices.length > 0 && (
                <div className="remote-display-group-controls border-top mt-3 pt-3">
                  <div className="remote-display-muted small fw-semibold mb-2">
                    Controls for selected screens · {selectedGlobalAgendaItem.agendaEvent?.name || selectedGlobalAgendaItem.item_title || 'Agenda item'}
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {isTimerManager && selectedGlobalAgendaItem.agendaEvent?.eventType !== 'poll' && (
                      <Select
                        className="flex-grow-1"
                        styles={selectStyles}
                        options={presenterOptions}
                        value={presenterOptions.flatMap((group) => group.options).find((option) => (
                          String(option.value) === String(
                            selectedGlobalAgendaItem.assignedUser?.id
                            || selectedGlobalAgendaItem.assignedUser?._id
                            || selectedGlobalAgendaItem.assignedUser?.tmAuthUserId
                            || ''
                          )
                        )) || null}
                        onMenuOpen={getPresenters}
                        onChange={(option) => handlePresenterChange(globalAgendaItemId, option)}
                        isClearable
                        isSearchable
                        placeholder="Change assigned presenter"
                        aria-label="Change presenter for selected agenda item"
                      />
                    )}
                    {canControlDisplays && selectedGlobalAgendaItem.agendaEvent?.eventType !== 'poll' && (
                      <>
                        {selectedGlobalAgendaItem.activity?.type === 'debate' ? <DebateAgendaTimers
                          key={selectedGlobalAgendaItem.agendaItemId}
                          item={selectedGlobalAgendaItem} meeting={meeting} getMeeting={getMeeting}
                          isTimerManager={isTimerManager} isLocked={Boolean(meeting?.meetingDetails?.isLocked)}
                        /> : <>
                        <span
                          className="remote-display-clock"
                          aria-label={`Timer ${formatTime(selectedGlobalTimerState.time)}`}
                          style={{
                            backgroundColor: selectedGlobalTimerColors.background,
                            color: selectedGlobalTimerColors.foreground,
                          }}
                        >
                          {formatTime(selectedGlobalTimerState.time)}
                        </span>
                        <Button
                          size="sm"
                          variant={selectedGlobalTimerState.isPlaying ? 'danger' : 'success'}
                          onClick={() => toggleRowTimer(globalAgendaItemId)}
                          title={selectedGlobalTimerState.isPlaying ? 'Stop timer' : 'Start timer'}
                        >
                          <i className={`bi ${selectedGlobalTimerState.isPlaying ? 'bi-stop-fill' : 'bi-play-fill'} me-1`} aria-hidden="true" />
                          {selectedGlobalTimerState.isPlaying ? 'Stop timer' : Number(selectedGlobalTimerState.time) > 0 ? 'Resume timer' : 'Start timer'}
                        </Button>
                        </>}
                        <Button
                          size="sm"
                          variant={selectedGlobalAgendaItem.hideTitleAndSubtitle ? 'danger' : 'primary'}
                          onClick={() => toggleSelectedTitleVisibility(selectedGlobalAgendaItem)}
                          title={selectedGlobalAgendaItem.hideTitleAndSubtitle ? 'Reveal title and subtitle' : 'Hide title and subtitle'}
                        >
                          <i className="bi bi-text-center me-1" aria-hidden="true" />
                          {selectedGlobalAgendaItem.hideTitleAndSubtitle ? 'Reveal title' : 'Hide title'}
                        </Button>
                      </>
                    )}
                    {canControlDisplays && (
                      <>
                        <Button
                          size="sm"
                          variant={selectedConnectedDevices.every((device) => displaySelections[device.deviceId]?.showTimer !== false) ? 'outline-primary' : 'primary'}
                          onClick={() => setSelectedDisplaysTimerVisibility(!selectedConnectedDevices.every((device) => displaySelections[device.deviceId]?.showTimer !== false))}
                          title="Toggle clock and timer display on selected screens"
                        >
                          <i className="bi bi-clock me-1" aria-hidden="true" />
                          {selectedConnectedDevices.every((device) => displaySelections[device.deviceId]?.showTimer !== false) ? 'Hide clock' : 'Show clock'}
                        </Button>
                      </>
                    )}
                  </div>

                  {selectedGlobalPoll && canManagePolls && (
                    <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                      <span className="small">
                        Poll status: <strong className="text-uppercase" style={{ color: getPollStatusColor(selectedGlobalPoll.status) }}>{selectedGlobalPoll.status}</strong>
                      </span>
                      {['ready', 'active', 'closed', 'displayed'].map((statusKey) => (
                        <Button
                          key={statusKey}
                          size="sm"
                          variant={selectedGlobalPoll.status === statusKey ? 'success' : 'outline-secondary'}
                          disabled={Boolean(updatingPollStatusByItem[selectedGlobalPoll._id])}
                          onClick={() => handlePollStatusChange(selectedGlobalPoll._id, statusKey)}
                        >
                          {statusKey}
                        </Button>
                      ))}
                      <Button size="sm" variant="outline-danger" onClick={() => handleDeletePoll(selectedGlobalPoll._id)}>
                        <i className="bi bi-trash me-1" aria-hidden="true" />Delete poll
                      </Button>
                    </div>
                  )}

                  {selectedGlobalAgendaItem.agendaEvent?.eventType === 'poll' && !selectedGlobalPoll && canManagePolls && (
                    <div className="mt-2">
                      <Button
                        size="sm"
                        variant="success"
                        onClick={() => togglePollForm(globalAgendaItemId)}
                      >
                        {pollFormOpenByItem[globalAgendaItemId] ? 'Cancel poll creation' : `Create Poll (${selectedGlobalAgendaItem.agendaEvent?.name?.replace(/^Poll\s*/i, '') || 'Poll'})`}
                      </Button>
                      {pollFormOpenByItem[globalAgendaItemId] && (
                        <div className="d-flex flex-column gap-1 mt-2">
                          {orderedAgenda.filter((item) => String(item.agendaItemId) !== String(globalAgendaItemId)).map((candidateItem) => (
                            <Form.Check
                              key={candidateItem.agendaItemId}
                              type="checkbox"
                              className="remote-display-muted"
                              checked={(selectedPollCandidatesByItem[globalAgendaItemId] || []).includes(candidateItem.agendaItemId)}
                              label={`${candidateItem.assignedUser?.firstName ? `${candidateItem.assignedUser.firstName} ${candidateItem.assignedUser.lastName || ''}`.trim() : 'TBA'} (${candidateItem.meetingRole?.name || candidateItem.agendaEvent?.name || 'Item'})`}
                              onChange={() => togglePollCandidate(globalAgendaItemId, candidateItem.agendaItemId)}
                            />
                          ))}
                          <Button
                            size="sm"
                            className="align-self-start"
                            disabled={Boolean(creatingPollByItem[globalAgendaItemId]) || !(selectedPollCandidatesByItem[globalAgendaItemId] || []).length}
                            onClick={() => handleCreatePoll(globalAgendaItemId, selectedGlobalAgendaItem)}
                          >
                            {creatingPollByItem[globalAgendaItemId] ? 'Creating poll…' : 'Save & Create Poll'}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedGlobalContestParticipants.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      <Button
                        size="sm"
                        variant="outline-light"
                        onClick={() => selectedConnectedDevices.forEach((device) => setAllRemoteParticipantsVisibility(device.deviceId, globalAgendaItemId, selectedGlobalContestParticipants, true))}
                      >
                        Show all participants
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-light"
                        onClick={() => selectedConnectedDevices.forEach((device) => setAllRemoteParticipantsVisibility(device.deviceId, globalAgendaItemId, selectedGlobalContestParticipants, false))}
                      >
                        Hide all participants
                      </Button>
                      {selectedGlobalContestParticipants.map((participant, index) => {
                        const visibleOnAll = selectedConnectedDevices.every((device) => participantVisibilityByDevice[device.deviceId]?.[participant.id] === true);
                        return (
                          <Button
                            key={participant.id}
                            size="sm"
                            variant={visibleOnAll ? 'primary' : 'outline-light'}
                            onClick={() => selectedConnectedDevices.forEach((device) => {
                              const isVisible = participantVisibilityByDevice[device.deviceId]?.[participant.id] === true;
                              if (isVisible === visibleOnAll) toggleRemoteParticipant(device.deviceId, globalAgendaItemId, participant.id);
                            })}
                            aria-label={`${visibleOnAll ? 'Hide' : 'Show'} participant ${index + 1} on selected screens`}
                          >
                            {index + 1}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
          {!registrationError && devices.length > 0 && <div className="remote-display-device-list">
            {orderedDevices.map((device) => {
              const selection = displaySelections[device.deviceId] || { agendaItemId: '', showTimer: true };
              const selectedTimerState = getSelectedTimerState(selection.agendaItemId);
              const selectedAgendaItem = orderedAgenda.find((item) => String(item.agendaItemId) === String(selection.agendaItemId));
              const selectedContestParticipants = contestParticipantsByItem[selection.agendaItemId] || [];
              const selectedCapability = getDeviceCapability(device, selection.agendaItemId);
              const visibleAgenda = orderedAgenda.filter((item) => Boolean(getDeviceCapability(device, item.agendaItemId)));
              const visibleAgendaIndex = visibleAgenda.findIndex((item) => String(item.agendaItemId) === String(selection.agendaItemId));
              const canControlDeviceTimer = device.capabilities?.includes('timer') && isTimerManager;
              const timerColorSource = {
                time: selectedTimerState.time,
                agendaItem: { agendaEvent: selectedAgendaItem?.agendaEvent || {} },
              };
              const selectedTimerColors = getTimerColors(
                timerColorSource.time,
                timerColorSource.agendaItem.agendaEvent
              );
              return (
                <Card key={device.deviceId} className="remote-display-device remote-display-device--card">
                  <Card.Body>
                  <div
                    className="remote-display-device-header d-flex justify-content-between align-items-center mb-2"
                    onClick={() => toggleDeviceCollapsed(device.deviceId)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={!collapsedDeviceIds[device.deviceId]}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggleDeviceCollapsed(device.deviceId);
                      }
                    }}
                  >
                    <Form.Check
                      type="checkbox"
                      className="me-2"
                      checked={Boolean(selectedDeviceIds[device.deviceId])}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => setSelectedDeviceIds((current) => ({
                        ...current,
                        [device.deviceId]: event.target.checked,
                      }))}
                      aria-label={`Select ${device.label} for group display`}
                    />
                    <div className="min-width-0">
                      <div className="fw-bold text-truncate">
                        {device.label}
                      </div>
                      <div className="remote-display-muted small">
                        Connection: {device.connected ? 'Connected now' : 'Not connected'} · Supports agenda, timers, and polls
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="link"
                      className="remote-display-collapse-toggle"
                      onClick={(event) => { event.stopPropagation(); toggleDeviceCollapsed(device.deviceId); }}
                      title={collapsedDeviceIds[device.deviceId] ? `Expand ${device.label}` : `Collapse ${device.label}`}
                      aria-label={collapsedDeviceIds[device.deviceId] ? `Expand ${device.label}` : `Collapse ${device.label}`}
                    >
                      <i className={`bi ${collapsedDeviceIds[device.deviceId] ? 'bi-chevron-down' : 'bi-chevron-up'}`} aria-hidden="true" />
                    </Button>
                  </div>
                  {!collapsedDeviceIds[device.deviceId] && (
                  <>
                  <div className="remote-display-muted small mb-2">
                    {device.instanceDisplay ? 'Meeting tab' : `Display ID: ${device.deviceId.slice(0, 6).toUpperCase()}`}
                  </div>
                  <Form.Select
                    className="mb-2"
                    value={selection.agendaItemId}
                    onChange={(event) => updateDisplaySelection(device.deviceId, { agendaItemId: event.target.value })}
                    aria-label={`Agenda item for ${device.label}`}
                  >
                    <option value="">Select agenda item</option>
                    {(Array.isArray(agenda) ? agenda : []).filter((item) => Boolean(getDeviceCapability(device, item.agendaItemId))).map((item) => (
                      <option key={item.agendaItemId} value={item.agendaItemId}>
                        {item.position}. {item.agendaEvent?.name || item.item_title || 'Agenda item'}
                      </option>
                    ))}
                  </Form.Select>
                  {selectedAgendaItem && (device.allowedCapabilities || device.capabilities || []).includes(selectedCapability) && (
                    selectedAgendaItem.agendaEvent?.eventType === 'poll' ? (
                      (() => {
                        const matchingPoll = polls.find((p) => p.title === selectedAgendaItem.agendaEvent?.name);
                        const isFormOpen = Boolean(pollFormOpenByItem[selection.agendaItemId]);
                        const isCreating = Boolean(creatingPollByItem[selection.agendaItemId]);
                        const selectedCandidates = selectedPollCandidatesByItem[selection.agendaItemId] || [];

                        return (
                          <div className="mb-2">
                            <div className="remote-display-muted small mb-1">Poll Controls:</div>
                            {matchingPoll ? (
                              <div>
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <span className="small">
                                    Status: <strong className="text-uppercase" style={{ color: getPollStatusColor(matchingPoll.status) }}>{matchingPoll.status}</strong>
                                  </span>
                                  {canManagePolls && (
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      className="py-0 px-2"
                                      style={{ fontSize: '0.75rem' }}
                                      onClick={() => handleDeletePoll(matchingPoll._id)}
                                      title="Delete poll"
                                    >
                                      <i className="bi bi-trash me-1" />Delete
                                    </Button>
                                  )}
                                </div>
                                <div className="d-flex gap-1 flex-wrap">
                                  {['ready', 'active', 'closed', 'displayed'].map((statusKey) => {
                                    const isCurrent = matchingPoll.status === statusKey;
                                    const variantMap = {
                                      ready: isCurrent ? 'success' : 'outline-success',
                                      active: isCurrent ? 'warning' : 'outline-warning',
                                      closed: isCurrent ? 'danger' : 'outline-danger',
                                      displayed: isCurrent ? 'info' : 'outline-info',
                                    };
                                    return (
                                      <Button
                                        key={statusKey}
                                        size="sm"
                                        variant={variantMap[statusKey]}
                                        disabled={!canManagePolls || updatingPollStatusByItem[matchingPoll._id]}
                                        onClick={() => handlePollStatusChange(matchingPoll._id, statusKey)}
                                        style={{
                                          flex: 1,
                                          minWidth: '65px',
                                          textTransform: 'capitalize',
                                          fontWeight: isCurrent ? 700 : 500,
                                          boxShadow: isCurrent ? '0 0 8px rgba(255,255,255,0.25)' : 'none',
                                        }}
                                      >
                                        {statusKey}
                                      </Button>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : (
                              <div>
                                <Button
                                  size="sm"
                                  variant="success"
                                  className="w-100 mb-2"
                                  disabled={!canManagePolls}
                                  onClick={() => togglePollForm(selection.agendaItemId)}
                                >
                                  {isFormOpen
                                    ? 'Cancel Poll Creation'
                                    : `Create Poll (${selectedAgendaItem.agendaEvent?.name?.replace(/^Poll\s*/i, '') || selectedAgendaItem.agendaEvent?.name || 'Poll'})`}
                                </Button>
                                {isFormOpen && (
                                  <div
                                    className="p-2 mb-2 rounded"
                                    style={{
                                      backgroundColor: 'rgba(0,0,0,0.3)',
                                      border: '1px solid #444',
                                      maxHeight: '260px',
                                      overflowY: 'auto',
                                    }}
                                  >
                                    <div className="small text-muted mb-2">Select Candidates / Agenda Items:</div>
                                    <div className="d-flex flex-column gap-1 mb-2">
                                      {orderedAgenda
                                        .filter((item) => String(item.agendaItemId) !== String(selection.agendaItemId))
                                        .map((candidateItem) => {
                                          const candidateSelected = selectedCandidates.includes(candidateItem.agendaItemId);
                                          const candidateInitials = getInitialsFromName(candidateItem.assignedUser?.firstName, candidateItem.assignedUser?.lastName, 'T');
                                          const candidateAvatar = resolveAvatarSrc(candidateItem.assignedUser?.avatarUrl || candidateItem.assignedUser?.avatar);
                                          return (
                                            <div
                                              key={candidateItem.agendaItemId}
                                              onClick={() => togglePollCandidate(selection.agendaItemId, candidateItem.agendaItemId)}
                                              className="d-flex align-items-center p-1 rounded"
                                              style={{
                                                cursor: 'pointer',
                                                backgroundColor: candidateSelected ? 'rgba(0, 65, 101, 0.5)' : 'rgba(255,255,255,0.05)',
                                                border: `1px solid ${candidateSelected ? '#0d6efd' : 'transparent'}`,
                                              }}
                                            >
                                              <Form.Check
                                                type="checkbox"
                                                checked={candidateSelected}
                                                onChange={() => {}}
                                                className="me-2 pointer-events-none"
                                              />
                                              {candidateAvatar ? (
                                                <img
                                                  src={candidateAvatar}
                                                  alt=""
                                                  style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }}
                                                  className="me-2"
                                                />
                                              ) : (
                                                <span
                                                  style={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: '50%',
                                                    background: '#555',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 700,
                                                  }}
                                                  className="me-2"
                                                >
                                                  {candidateInitials || '?'}
                                                </span>
                                              )}
                                              <div className="small text-truncate">
                                                <span className="fw-semibold">
                                                  {candidateItem.assignedUser?.firstName
                                                    ? `${candidateItem.assignedUser.firstName} ${candidateItem.assignedUser.lastName}`.trim()
                                                    : 'TBA'}
                                                </span>
                                                <span className="text-muted ms-1">
                                                  ({candidateItem.meetingRole?.name || candidateItem.agendaEvent?.name || 'Item'})
                                                </span>
                                              </div>
                                            </div>
                                          );
                                        })}
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="primary"
                                      className="w-100"
                                      disabled={isCreating || selectedCandidates.length === 0}
                                      onClick={() => handleCreatePoll(selection.agendaItemId, selectedAgendaItem)}
                                    >
                                      {isCreating ? 'Creating Poll...' : 'Save & Create Poll'}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()
                    ) : (
                      <div className="mb-2">
                        <div className="remote-display-muted small mb-1">Assigned Presenter:</div>
                        {isTimerManager ? (
                          presenterDropdownOpen[selection.agendaItemId] ? (
                            <div className="d-flex align-items-center gap-1">
                              <div className="flex-grow-1">
                                <Select
                                  autoFocus
                                  defaultMenuIsOpen
                                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                  menuPosition="fixed"
                                  options={presenterOptions}
                                  className="formInput"
                                  isLoading={loadingPresenterChange[selection.agendaItemId]}
                                  value={
                                    presenterOptions
                                      .flatMap((group) => group.options)
                                      .find((option) => String(option.value) === String(
                                        selectedAgendaItem?.assignedUser?.id ||
                                        selectedAgendaItem?.assignedUser?._id ||
                                        selectedAgendaItem?.assignedUser?.tmAuthUserId ||
                                        ''
                                      )) || null
                                  }
                                  onChange={(option) => handlePresenterChange(selection.agendaItemId, option)}
                                  isClearable
                                  isSearchable
                                  styles={selectStyles}
                                  placeholder="Select a presenter"
                                />
                              </div>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => setPresenterDropdownOpen((current) => ({ ...current, [selection.agendaItemId]: false }))}
                                title="Cancel"
                                aria-label="Cancel presenter selection"
                                style={{ minHeight: '38px', padding: '0 10px' }}
                              >
                                <i className="bi bi-x-lg" />
                              </Button>
                            </div>
                          ) : (
                            <div
                              className="form-control d-flex justify-content-between align-items-center"
                              onClick={() => {
                                getPresenters();
                                setPresenterDropdownOpen((current) => ({ ...current, [selection.agendaItemId]: true }));
                              }}
                              style={{
                                cursor: 'pointer',
                                backgroundColor: '#23272b',
                                borderColor: '#444',
                                color: selectedAgendaItem?.assignedUser ? '#fff' : '#aaa',
                                minHeight: '38px',
                              }}
                              title="Click to change presenter"
                            >
                              <span>
                                {selectedAgendaItem?.assignedUser
                                  ? `${selectedAgendaItem.assignedUser.firstName} ${selectedAgendaItem.assignedUser.lastName}`.trim()
                                  : 'TBA (Click to assign)'}
                              </span>
                              <i className="bi bi-pencil-square text-muted" style={{ fontSize: '0.85rem' }} />
                            </div>
                          )
                        ) : (
                          <div
                            className="form-control"
                            style={{
                              backgroundColor: '#23272b',
                              borderColor: '#444',
                              color: selectedAgendaItem?.assignedUser ? '#fff' : '#aaa',
                              minHeight: '38px',
                            }}
                          >
                            {selectedAgendaItem?.assignedUser
                              ? `${selectedAgendaItem.assignedUser.firstName} ${selectedAgendaItem.assignedUser.lastName}`.trim()
                              : 'TBA'}
                          </div>
                        )}
                      </div>
                    )
                  )}
                  {canControlDeviceTimer && selectedAgendaItem?.activity?.type === 'debate' && (
                    <div className="my-3" role="group" aria-label={`Debate controls for ${device.label}`}>
                      <DebateAgendaTimers key={selectedAgendaItem.agendaItemId}
                        item={selectedAgendaItem} meeting={meeting} getMeeting={getMeeting}
                        isTimerManager={isTimerManager} isLocked={Boolean(meeting?.meetingDetails?.isLocked)} />
                    </div>
                  )}
                  <div className="d-flex gap-2 remote-display-actions">
                    {!device.registeredDevice && selectedContestParticipants.length > 0 && <>
                      <Button size="sm" className="remote-display-participant-toggle-all" variant="outline-light" onClick={() => setAllRemoteParticipantsVisibility(device.deviceId, selection.agendaItemId, selectedContestParticipants, true)} title="Show all participants" aria-label={`Show all participants on ${device.label}`}>Show all</Button>
                      <Button size="sm" className="remote-display-participant-toggle-all" variant="outline-light" onClick={() => setAllRemoteParticipantsVisibility(device.deviceId, selection.agendaItemId, selectedContestParticipants, false)} title="Hide all participants" aria-label={`Hide all participants on ${device.label}`}>Hide all</Button>
                    </>}
                    {selectedContestParticipants.map((participant, index) => {
                      const displayNumber = index + 1;
                      const isVisible = participantVisibilityByDevice[device.deviceId]?.[participant.id] === true;
                      return (
                        <Button
                          key={participant.id}
                          size="sm"
                          className={`remote-display-participant-toggle${isVisible ? ' is-visible' : ''}`}
                          variant="outline-light"
                          onClick={() => toggleRemoteParticipant(device.deviceId, selection.agendaItemId, participant.id)}
                          title={`${isVisible ? 'Hide' : 'Show'} participant ${displayNumber}: ${participant.name}`}
                          aria-label={`${isVisible ? 'Hide' : 'Show'} participant ${displayNumber}: ${participant.name} on ${device.label}`}
                          aria-pressed={isVisible}
                        >
                          {displayNumber}
                        </Button>
                      );
                    })}
                    <Button
                      size="sm"
                      className="remote-display-secondary-button"
                      variant="outline-light"
                      disabled={visibleAgendaIndex <= 0}
                      onClick={() => navigateDisplay(device.deviceId, -1)}
                      title="Previous agenda item"
                      aria-label={`Previous agenda item on ${device.label}`}
                    >
                      <i className="bi bi-chevron-left" aria-hidden="true" />
                    </Button>
                    <Button size="sm" onClick={() => sendDisplayCommand(device.deviceId)}>Display</Button>
                    <Button className="remote-display-secondary-button" size="sm" variant="outline-secondary" onClick={() => sendDisplayCommand(device.deviceId, true)}>Close</Button>
                    <Button
                      size="sm"
                      className="remote-display-secondary-button"
                      variant="outline-light"
                      disabled={visibleAgendaIndex < 0 || visibleAgendaIndex >= visibleAgenda.length - 1}
                      onClick={() => navigateDisplay(device.deviceId, 1)}
                      title="Next agenda item"
                      aria-label={`Next agenda item on ${device.label}`}
                    >
                      <i className="bi bi-chevron-right" aria-hidden="true" />
                    </Button>
                    {canControlDeviceTimer && selectedAgendaItem?.activity?.type !== 'debate' && <span
                      className="remote-display-clock"
                      aria-label={`Timer ${formatTime(selectedTimerState.time)}`}
                      style={selection.agendaItemId ? {
                        backgroundColor: selectedTimerColors.background,
                        color: selectedTimerColors.foreground,
                      } : undefined}
                    >
                      {formatTime(selectedTimerState.time)}
                    </span>}
                    {canControlDeviceTimer && selectedAgendaItem?.activity?.type !== 'debate' && <Button
                      size="sm"
                      variant={selectedTimerState.isPlaying ? 'danger' : 'success'}
                      disabled={!selection.agendaItemId}
                      onClick={() => toggleRowTimer(selection.agendaItemId)}
                      title={!selection.agendaItemId ? 'Select an agenda item first' : selectedTimerState.isPlaying ? 'Stop timer' : 'Start timer'}
                    >
                      <i className={`bi ${selectedTimerState.isPlaying ? 'bi-stop-fill' : 'bi-play-fill'} me-1`} aria-hidden="true" />
                      {selectedTimerState.isPlaying ? 'Stop' : Number(selectedTimerState.time) > 0 ? 'Resume' : 'Start'}
                    </Button>}
                    {(device.instanceDisplay || !device.registeredDevice || isOfficerUser) && <Button
                      size="sm"
                      className="ms-2"
                      variant={selectedAgendaItem?.hideTitleAndSubtitle ? 'danger' : 'primary'}
                      disabled={!selectedAgendaItem}
                      onClick={() => toggleTitleVisibility(device.deviceId, selectedAgendaItem)}
                      title={selectedAgendaItem?.hideTitleAndSubtitle ? 'Reveal title and subtitle' : 'Hide title and subtitle'}
                      aria-label={selectedAgendaItem?.hideTitleAndSubtitle ? `Reveal title and subtitle on ${device.label}` : `Hide title and subtitle on ${device.label}`}
                    >
                      <i className="bi bi-text-center" aria-hidden="true" />
                    </Button>}
                    {canControlDeviceTimer && <Button
                      size="sm"
                      variant={selection.showTimer ? 'primary' : 'danger'}
                      onClick={() => toggleDisplayTimerVisibility(device.deviceId)}
                      title={`${selection.showTimer ? 'Hide' : 'Show'} clock, timer controls, colors, and bell on the remote display`}
                      aria-label={`${selection.showTimer ? 'Hide' : 'Show'} clock, timer controls, colors, and bell on ${device.label}`}
                      aria-pressed={selection.showTimer}
                    >
                      <i className={`bi ${selection.showTimer ? 'bi-clock' : 'bi-clock-history'}`} aria-hidden="true" />
                    </Button>}
                  </div>
                  </>
                  )}
                  </Card.Body>
                </Card>
              );
            })}
          </div>}
        </>
      )}
      {!remoteDisplaysOnly && <Row key='001'>
        <Col>
          {timers.length > 0 ? (
            timers
              .sort((a, b) => a.agendaItem.position - b.agendaItem.position)
              .map((timer) => {
                const agendaItemId = String(timer?.agendaItem?.id || timer?.agendaItem?._id || '');
                const linkedAgendaItem = agendaByItemId.get(agendaItemId);
                const activity = linkedAgendaItem?.activity || timer?.agendaItem?.activity;
                const segmentId = String(timer.segmentId || '');
                const turn = segmentId ? getDebateTurns(activity, linkedAgendaItem?.assignedUser)
                  .find(entry => entry.id === segmentId) : null;
                const segment = turn || activity?.segments?.find(entry => entry.id === segmentId);
                const currentTime = timerStates[timerStateKey(agendaItemId, segmentId)]?.time ?? Number(timer?.time || 0);
                const currentAgendaEvent = (segment
                  ? getActivityTimingLimits({ type: 'debate', segments: [segment] })
                  : !segmentId && getActivityTimingLimits(activity))
                  || linkedAgendaItem?.agendaEvent || timer?.agendaItem?.agendaEvent || {};
                const timerColors = getTimerColors(currentTime, currentAgendaEvent);
                const presenter = segmentId
                  ? segment?.speaker
                  : linkedAgendaItem?.assignedUser || timer?.agendaItem?.assignedUser;
                const presenterName = presenter
                  ? `${presenter.firstName || ''} ${presenter.lastName || ''}`.trim() || timer.name || 'TBA'
                  : timer.name || 'TBA';
                const presenterInitials = getInitialsFromName(
                  presenter?.firstName || timer?.name || '',
                  presenter?.lastName || '',
                  'T'
                );

                return (
                  <Card
                    key={timer._id}
                    role="group"
                    aria-label={`${segment ? `${segment.title} - ` : ''}${presenterName} timer`}
                    className="timer-report-card mb-2"
                    style={{
                      backgroundColor: timerColors.background,
                      color: timerColors.foreground
                    }}
                  >
                    <Card.Body>
                      <div className="timer-report-card-row">
                        <div className="timer-report-main">
                          <div className="timer-report-avatar">
                            <TimerReportAvatar user={presenter} meeting={meeting} initials={presenterInitials} />
                          </div>

                          <div className="timer-report-text">
                            <div className="timer-report-event-name">{`${timer?.agendaItem?.agendaEvent?.name || 'Agenda item'}`}</div>
                            {segment && <div className="small">{segment.title}{turn?.side ? ` - ${turn.side.toUpperCase()}` : ''}</div>}
                            <div className="timer-report-presenter-name">{presenterName}</div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center">
                          <div className="timer-report-time">{formatTime(currentTime)}</div>
                          {isTimerManager && (
                            <Button
                              variant="outline-light"
                              size="sm"
                              className="ms-2"
                              title="Reset timer"
                              aria-label="Reset timer"
                              disabled={resettingTimerId === String(timer?._id || '')}
                              onClick={() => handleResetTimer(timer)}
                              style={{ lineHeight: 1, padding: '0.2rem 0.45rem' }}
                            >
                              <i className="bi bi-eraser-fill" aria-hidden='true'></i>
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                )
              })
          ) : (
            <p className='mb-0 profile-overview-empty-message'>No time recorded.</p>
          )}
        </Col>
      </Row>}
    </div>
  )
}